import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  AccountPaymentInformation,
  CardAddResponseParameters,
  CCardInterface
} from 'types';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import api from '../api/cards.api';

const CARDS_LIST = 'CARDS_LIST';

export const paymentMethodsApi = createApi({
  reducerPath: 'paymentMethodsApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),
  tagTypes: [CARDS_LIST],
  endpoints: (builder) => ({
    getPaymentMethods: builder.query<AccountPaymentInformation[], string>({
      queryFn: async () => {
        try {
          const { data } = await api.getClAccounts();

          return { data };
        } catch (error) {
          console.error(`Error while get client accounts: ${error.message}`);

          return { error: error.response?.data?.error || error.message };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'CARDS_LIST' as const, id })),
              CARDS_LIST
            ]
          : [CARDS_LIST]
    }),
    addedPaymentMethod: builder.mutation<
      CardAddResponseParameters,
      CCardInterface
    >({
      queryFn: async (creditCardData: CCardInterface) => {
        try {
          const { data } = await api.addCCard(creditCardData);

          return { data };
        } catch (error) {
          console.error(`Error while add credit card: ${error.message}`);

          return {
            error:
              error.response?.data?.error || error.message || 'Unknown error'
          };
        }
      },
      invalidatesTags: (res) => (!res?.error ? [CARDS_LIST] : [])
    }),
    updatePaymentMethod: builder.mutation<
      CardAddResponseParameters,
      Partial<CCardInterface>
    >({
      queryFn: async (creditCardData: Partial<CCardInterface>) => {
        try {
          const { data } = await api.updateCCard(creditCardData);

          return { data };
        } catch (error) {
          console.error(`Error while update credit card: ${error.message}`);

          return {
            error:
              error.response?.data?.error?.toString() ||
              error.message ||
              'Unknown error'
          };
        }
      },
      invalidatesTags: (res) => (res?.action ? [CARDS_LIST] : [])
    }),
    deletePaymentMethod: builder.mutation<CardAddResponseParameters, number>({
      queryFn: async (id: number) => {
        try {
          const { data } = await api.deleteCCard(id);

          return { data };
        } catch (error) {
          console.error(`Error while delete credit card: ${error.message}`);
          const errorMessage =
            error.response?.data?.error || error.message || 'Unknown error';

          return { error: errorMessage };
        }
      },
      async onQueryStarted(id, { queryFulfilled, dispatch }) {
        await queryFulfilled;

        dispatch(
          paymentMethodsApi.util.updateQueryData(
            'getPaymentMethods',
            '',
            (draft) => {
              draft = draft.filter((card) => card.id !== id);
              return draft;
            }
          )
        );
      }
    })
  })
});

export const {
  useGetPaymentMethodsQuery,
  useAddedPaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useDeletePaymentMethodMutation
} = paymentMethodsApi;
